import {
  Button,
  FormInput,
  FormRoot,
  FormTextarea,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { Settings, Sparkles } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";

type DepartmentItem = { id: string; name: string };
type PositionItem = { id: string; title: string };
type StepTypeItem = { id: string; name: string };

const FlowStepFormSchema = z.object({
  stepNumber: z.preprocess(
    (val) => {
      if (val === "" || val === null || val === undefined) return undefined;
      const num = typeof val === "string" ? Number(val) : val;
      return typeof num === "number" && !Number.isNaN(num)
        ? Math.floor(num)
        : undefined;
    },
    z.number().int().min(1, "ກະລຸນາໃສ່ລຳດັບຂັ້ນຕອນ"),
  ),
  title: z.string().min(1, "ກະລຸນາໃສ່ຊື່ຂັ້ນຕອນ"),
  description: z.string().optional().nullable(),
  responsiblePositionId: z
    .union([z.string(), z.null()])
    .transform((val) => (val === "" ? null : val))
    .nullable()
    .optional(),
  departmentId: z
    .union([z.string(), z.null()])
    .transform((val) => (val === "" ? null : val))
    .nullable()
    .optional(),
  stepTypeId: z
    .union([z.string(), z.null()])
    .transform((val) => (val === "" ? null : val))
    .nullable()
    .optional(),
  notes: z.string().optional().nullable(),
});

export type FlowStepFormValues = z.infer<typeof FlowStepFormSchema>;

export function FlowStepForm({
  initialValues,
  onSubmit,
  submitting,
  flowDepartmentId,
  existingStepsCount = 0,
  onManageStepTypes,
}: {
  initialValues?: Partial<FlowStepFormValues>;
  onSubmit: (values: FlowStepFormValues) => void;
  submitting?: boolean;
  flowDepartmentId?: string | null;
  existingStepsCount?: number;
  onManageStepTypes?: () => void;
}) {
  const methods = RHF.useForm<FlowStepFormValues>({
    resolver: zodResolver(FlowStepFormSchema) as any,
    defaultValues: {
      stepNumber: existingStepsCount + 1,
      title: "",
      description: null,
      responsiblePositionId: null,
      stepTypeId: null,
      notes: null,
      ...initialValues,
      // Always use flow's department, override initialValues if needed
      departmentId: flowDepartmentId ?? initialValues?.departmentId ?? null,
    },
  });

  // Update departmentId when flowDepartmentId changes
  useEffect(() => {
    if (flowDepartmentId) {
      methods.setValue("departmentId", flowDepartmentId);
    }
  }, [flowDepartmentId, methods]);

  // Check if in dev mode
  const isDevMode =
    import.meta.hot !== undefined || import.meta.env?.MODE === "development";

  // Generate random data for auto fill
  const generateRandomData = async () => {
    const titles = [
      "ກວດສອບສິນຄ້າ",
      "ບັນຍັດສິນຄ້າ",
      "ຈັດສົ່ງສິນຄ້າ",
      "ຮັບສິນຄ້າ",
      "ກວດສອບຄຸນນະພາບ",
      "ອະນຸມັດຄຳສັ່ງຊື້",
      "ກຽມສິນຄ້າ",
      "ບັນທຶກຂໍ້ມູນ",
      "ກວດສອບສາງ",
      "ຈັດການສິນຄ້າຄົງຄັງ",
    ];

    const descriptions = [
      "ກວດສອບສິນຄ້າກ່ອນຈັດສົ່ງ",
      "ບັນຍັດສິນຄ້າຕາມຄຳສັ່ງຊື້",
      "ຈັດສົ່ງສິນຄ້າໄປຍັງລູກຄ້າ",
      "ຮັບສິນຄ້າເຂົ້າສາງ",
      "ກວດສອບຄຸນນະພາບສິນຄ້າ",
      "ອະນຸມັດຄຳສັ່ງຊື້ຈາກລູກຄ້າ",
      "ກຽມສິນຄ້າສຳລັບຈັດສົ່ງ",
      "ບັນທຶກຂໍ້ມູນການດຳເນີນງານ",
      "ກວດສອບສະຖານະສາງ",
      "ຈັດການສິນຄ້າຄົງຄັງໃນສາງ",
    ];

    const notesOptions = [
      "ຕ້ອງກວດສອບຄຸນນະພາບຢ່າງລະມັດລະວັງ",
      "ຕ້ອງບັນຍັດຕາມລຳດັບຄຳສັ່ງຊື້",
      "ຕ້ອງຈັດສົ່ງພາຍໃນ 24 ຊົ່ວໂມງ",
      "ຕ້ອງກວດສອບຈຳນວນກ່ອນຮັບ",
      "ຕ້ອງບັນທຶກຂໍ້ມູນຢ່າງຖືກຕ້ອງ",
      "ຕ້ອງກວດສອບວັນໝົດອາຍຸ",
      "ຕ້ອງຈັດການສິນຄ້າຄົງຄັງກ່ອນ",
      null,
      null,
    ];

    // Get first step type for stepTypeId
    let stepTypeId: string | null = null;
    try {
      const stepTypesResult = await fetchLookupForInfinite<StepTypeItem>(
        `${config.apiUrl}/flows/step-types/lookup`,
        { search: "", pageParam: 0 },
      );
      const firstStepType = stepTypesResult.items?.[0];
      if (firstStepType?.id) {
        stepTypeId = firstStepType.id;
      }
    } catch {
      // If fetching step types fails, leave stepTypeId null (user can select manually)
    }

    // Get first position for responsiblePositionId (if department is available)
    let responsiblePositionId: string | null = null;
    if (flowDepartmentId) {
      try {
        const url = new URL(`${config.apiUrl}/flows/positions/lookup`);
        url.searchParams.set("departmentId", flowDepartmentId);
        url.searchParams.set("limit", "1");
        url.searchParams.set("skip", "0");

        const response = await fetch(url.toString(), {
          credentials: "include",
        });
        const data = await response.json();
        const firstPosition = Array.isArray(data.items) ? data.items[0] : null;
        if (firstPosition?.id) {
          responsiblePositionId = firstPosition.id;
        }
      } catch {
        // If fetching positions fails, leave responsiblePositionId null
      }
    }

    const randomTitle = titles[Math.floor(Math.random() * titles.length)] ?? "";
    const randomDescription =
      descriptions[Math.floor(Math.random() * descriptions.length)] ?? null;
    const randomNotes =
      notesOptions[Math.floor(Math.random() * notesOptions.length)] ?? null;

    // Set form values
    methods.setValue("stepNumber", existingStepsCount + 1);
    methods.setValue("title", randomTitle);
    if (randomDescription) {
      methods.setValue("description", randomDescription);
    }
    if (stepTypeId) {
      methods.setValue("stepTypeId", stepTypeId);
    }
    if (responsiblePositionId) {
      methods.setValue("responsiblePositionId", responsiblePositionId);
    }
    if (randomNotes) {
      methods.setValue("notes", randomNotes);
    }
    // departmentId is already set from flowDepartmentId via useEffect

    toast.success("Auto-filled form with random data");
  };

  return (
    <FormRoot<FlowStepFormValues>
      methods={methods}
      onSubmit={onSubmit}
      className="space-y-4"
    >
      <FormInput
        name="stepNumber"
        label="ລຳດັບຂັ້ນຕອນ"
        type="number"
        requiredMark
        placeholder="ຕົວຢ່າງ: 1, 2, 3"
      />
      <FormInput
        name="title"
        label="ຊື່ຂັ້ນຕອນ"
        requiredMark
        placeholder="ກະລຸນາໃສ່ຊື່ຂັ້ນຕອນ"
      />
      <FormTextarea
        name="description"
        label="ຄຳອະທິບາຍ"
        placeholder="ຄຳອະທິບາຍຂັ້ນຕອນ"
        rows={3}
      />
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">ປະເພດຂັ້ນຕອນ</span>
        </div>
        <div className="flex items-center gap-2">
          <FormInfiniteCombobox<StepTypeItem>
            name="stepTypeId"
            label=""
            queryKey={["flows-step-types"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/flows/step-types/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/flows/step-types/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກປະເພດຂັ້ນຕອນ (ບໍ່ບັງຄັບ)"
            clearable
          />
          {onManageStepTypes && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={onManageStepTypes}
              title="ຈັດການປະເພດຂັ້ນຕອນ"
            >
              <Settings className="mr-1.5 h-3.5 w-3.5" />
              <span className="text-xs">ຈັດການ</span>
            </Button>
          )}
        </div>
      </div>
      <FormInfiniteCombobox<DepartmentItem>
        name="departmentId"
        label="ພະແນກ"
        queryKey={[
          "flows-departments",
          ...(flowDepartmentId ? [flowDepartmentId] : []),
        ]}
        queryFn={(args) =>
          fetchLookupForInfinite(
            `${config.apiUrl}/flows/departments/lookup`,
            args,
          )
        }
        preloadQueryFn={(id) =>
          hydrateLookupItem(`${config.apiUrl}/flows/departments/lookup`, id)
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        placeholder={flowDepartmentId ? "ພະແນກຂອງ Flow" : "ເລືອກພະແນກ (ບໍ່ບັງຄັບ)"}
        disabled={!!flowDepartmentId}
        clearable={!flowDepartmentId}
      />
      <FormInfiniteCombobox<PositionItem>
        name="responsiblePositionId"
        label="ຕໍາແໜ່ງຜູ້ຮັບຜິດຊອບ"
        queryKey={[
          "flows-positions",
          ...(flowDepartmentId ? [flowDepartmentId] : []),
        ]}
        queryFn={(args) => {
          const url = new URL(`${config.apiUrl}/flows/positions/lookup`);
          if (args.search && args.search.trim().length > 0) {
            url.searchParams.set("q", args.search);
          }
          if (flowDepartmentId) {
            url.searchParams.set("departmentId", flowDepartmentId);
          }
          const limit = 20;
          const page =
            typeof args.pageParam === "number" && args.pageParam > 0
              ? args.pageParam
              : 1;
          const skip = (page - 1) * limit;
          url.searchParams.set("limit", String(limit));
          url.searchParams.set("skip", String(skip));

          return fetch(url.toString(), { credentials: "include" })
            .then((r) => r.json())
            .then((data) => {
              const items = Array.isArray(data.items) ? data.items : [];
              const total =
                typeof data.total === "number" ? data.total : items.length;
              const nextPage = skip + limit < total ? page + 1 : null;

              return {
                items: items.map((item: { id: string; name: string }) => ({
                  id: item.id,
                  title: item.name,
                })),
                nextPage,
              };
            });
        }}
        preloadQueryFn={async (id) => {
          const item = await hydrateLookupItem<{ id: string; name: string }>(
            `${config.apiUrl}/flows/positions/lookup`,
            id,
          );
          if (!item) return null;
          return { id: item.id, title: item.name };
        }}
        getLabel={(item) => item.title}
        getValue={(item) => item.id}
        placeholder="ເລືອກຕໍາແໜ່ງ (ບໍ່ບັງຄັບ)"
        clearable
      />
      <FormTextarea
        name="notes"
        label="ຫມາຍເຫດ"
        placeholder="ຫມາຍເຫດເພີ່ມເຕີມ"
        rows={2}
      />

      <div className="flex items-center justify-between pt-4">
        {isDevMode && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={generateRandomData}
            className="text-xs"
          >
            <Sparkles className="mr-1 h-3 w-3" />
            Auto Fill (Dev)
          </Button>
        )}
        <div className="flex justify-end gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
